// =============================================================================
// live-events.js – DOM event bindings for the LIVE tab
// =============================================================================
// Extracted from live.js to separate rendering from event handling.
// All event listeners are bound via bindLiveEvents() called after patchDOM.
// =============================================================================

import { escapeHtml } from "./utils.js";
import { getStopName } from "./line-config.js";
import { openMap } from "./map.js";
import { dismissAlert } from "./alerts.js";
import { resolveFavoriteStop } from "./live-logic";

/**
 * Bind search chips click/delete events.
 * @param {HTMLElement} chipsWrapper
 * @param {Function} handleStopPick
 * @param {Function} getSearchHistoryChipsHTML - returns the HTML for chips
 */
export function bindChipEvents(chipsWrapper, handleStopPick, getSearchHistoryChipsHTML) {
  chipsWrapper.querySelectorAll("[data-chip-stop]").forEach((chip) => {
    if (chip.__has_click) return;
    chip.__has_click = true;
    chip.addEventListener("click", (e) => {
      if (e.target.closest("[data-chip-delete]")) return;
      handleStopPick(chip.dataset.chipStop);
    });
  });

  chipsWrapper.querySelectorAll("[data-chip-delete]").forEach((btn) => {
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const codeToDelete = btn.dataset.chipDelete;
      try {
        let history = JSON.parse(localStorage.getItem("trasporti_search_history") || "[]");
        history = history.filter((c) => c !== codeToDelete);
        localStorage.setItem("trasporti_search_history", JSON.stringify(history));
        chipsWrapper.innerHTML = getSearchHistoryChipsHTML();
        bindChipEvents(chipsWrapper, handleStopPick, getSearchHistoryChipsHTML);
      } catch (err) {
        console.warn(err);
      }
    });
  });
}

/**
 * Bind all interactive event listeners for the LIVE tab.
 * Called after patchDOM renders the live content.
 *
 * @param {HTMLElement} container - The live content container
 * @param {object} ctx - Context object with all dependencies
 * @param {Function} ctx.getArgs - Returns current { state, lineData, lineConfig, cfg, saveSettings }
 * @param {Function} ctx.renderLive - Re-render callback
 * @param {Function} ctx.buildSearchResults - Search results builder
 * @param {Function} ctx.getSearchHistoryChipsHTML - Search history chips HTML
 */
export function bindLiveEvents(container, ctx) {
  const { getArgs, renderLive, buildSearchResults, getSearchHistoryChipsHTML } = ctx;
  const { state, lineData, lineConfig, cfg, saveSettings } = getArgs();
  const focusCity = state.settings?.focusCity || cfg.defaults?.focusCity || "BT";

  // Force-sync each select's value to the user's stored preference
  container.querySelectorAll(".dep-stop-select").forEach((select) => {
    const lineId = select.dataset.line;
    const direction = select.dataset.dir;
    const preferred = resolveFavoriteStop(lineId, direction, state, cfg);
    if (preferred) {
      const hasOption = [...select.options].some((opt) => opt.value === preferred);
      if (hasOption && select.value !== preferred) {
        select.value = preferred;
      }
    }
  });

  // Dropdown dep-stop-select change event
  container.querySelectorAll(".dep-stop-select").forEach((select) => {
    if (!select.__has_stop_prop) {
      select.__has_stop_prop = true;
      select.addEventListener("click", (e) => e.stopPropagation());
      select.addEventListener("mousedown", (e) => {
        e.stopPropagation();
        select.__shown_at = Date.now();
      });
      select.addEventListener("mouseup", (e) => e.stopPropagation());
      select.addEventListener(
        "touchstart",
        () => {
          select.__shown_at = Date.now();
        },
        { passive: true }
      );
      select.addEventListener(
        "touchend",
        () => {
          select.__shown_at = Date.now();
        },
        { passive: true }
      );
    }

    if (!select.__has_blur) {
      select.__has_blur = true;
      select.addEventListener("blur", () => {
        setTimeout(() => {
          if (select.style.display && select.style.display !== "none") {
            const wrapper = select.closest(".dep-stop-edit-wrapper");
            if (wrapper) {
              const staticText = wrapper.querySelector(".dep-stop-name-text");
              const editBtn = wrapper.querySelector(".edit-dep-stop-btn");
              if (staticText && editBtn) {
                staticText.style.visibility = "";
                editBtn.style.visibility = "";
                select.style.display = "none";
              }
            }
          }
        }, 200);
      });
    }

    if (!select.__has_keydown) {
      select.__has_keydown = true;
      select.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          const wrapper = select.closest(".dep-stop-edit-wrapper");
          if (wrapper) {
            const editBtn = wrapper.querySelector(".edit-dep-stop-btn");
            const staticText = wrapper.querySelector(".dep-stop-name-text");
            if (staticText && editBtn) {
              staticText.style.visibility = "";
              editBtn.style.visibility = "";
              select.style.display = "none";
              select.blur();
            }
          }
        }
      });
    }

    if (select.__has_change) return;
    select.__has_change = true;
    select.addEventListener("change", (e) => {
      e.stopPropagation();
      const lineId = select.dataset.line;
      const direction = select.dataset.dir;
      const newStop = select.value;

      const wrapper = select.closest(".dep-stop-edit-wrapper");
      if (wrapper) {
        const staticText = wrapper.querySelector(".dep-stop-name-text");
        const editBtn = wrapper.querySelector(".edit-dep-stop-btn");
        if (staticText && editBtn) {
          staticText.style.visibility = "";
          editBtn.style.visibility = "";
          select.style.display = "none";
        }
      }

      const { state: s, saveSettings: save } = getArgs();
      const favoriteStops = structuredClone(s.settings.favoriteStops || {});
      if (!favoriteStops[lineId]) favoriteStops[lineId] = {};
      favoriteStops[lineId][direction] = newStop;
      save({ favoriteStops });

      showPremiumToast(
        `Fermata <strong>${escapeHtml(getStopName(newStop))}</strong> impostata come preferita per la linea ${lineId}!`,
        "success"
      );

      const wasFeatured = !!select.closest(".featured-card");
      if (!wasFeatured) {
        setTimeout(() => {
          const updatedCard = document.querySelector(`.line-card[data-card="live-${lineId}"]`);
          if (updatedCard) {
            updatedCard.scrollIntoView({ behavior: "smooth", block: "nearest" });
            updatedCard.style.transition = "box-shadow 0.3s ease";
            updatedCard.style.boxShadow = "0 0 0 2px var(--accent)";
            setTimeout(() => {
              updatedCard.style.boxShadow = "";
            }, 1500);
          }
        }, 100);
      }
    });
  });

  // Edit button
  container.querySelectorAll(".edit-dep-stop-btn").forEach((btn) => {
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const wrapper = btn.closest(".dep-stop-edit-wrapper");
      if (wrapper) {
        const staticText = wrapper.querySelector(".dep-stop-name-text");
        const selectEl = wrapper.querySelector(".dep-stop-select");
        if (staticText && selectEl) {
          staticText.style.visibility = "hidden";
          btn.style.visibility = "hidden";
          selectEl.style.display = "inline-block";
          selectEl.__shown_at = Date.now();
          selectEl.focus();
        }
      }
    });
  });

  // Click outside to close dep-stop-selects
  if (!window.__has_global_stop_click_outside) {
    window.__has_global_stop_click_outside = true;
    document.addEventListener("click", (e) => {
      const target = e.target;
      const activeEl = document.activeElement;
      setTimeout(() => {
        document.querySelectorAll(".dep-stop-select").forEach((select) => {
          if (select.style.display && select.style.display !== "none") {
            if (select.__shown_at && Date.now() - select.__shown_at < 700) return;
            if (
              target === select ||
              select.contains(target) ||
              target === document.body ||
              target === document.documentElement ||
              activeEl === select
            )
              return;
            const wrapper = select.closest(".dep-stop-edit-wrapper");
            if (wrapper && wrapper.contains(target)) return;
            if (wrapper) {
              const editBtn = wrapper.querySelector(".edit-dep-stop-btn");
              const staticText = wrapper.querySelector(".dep-stop-name-text");
              if (staticText && editBtn) {
                staticText.style.visibility = "";
                editBtn.style.visibility = "";
                select.style.display = "none";
              }
            }
          }
        });
      }, 150);
    });
  }

  // View timetable
  container.querySelectorAll(".view-timetable-btn").forEach((btn) => {
    if (btn.classList.contains("view-train-timetable-btn")) return;
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      state.timetableLine = btn.dataset.timetableLine;
      state.timetableDirection = btn.dataset.timetableDir;
      if (window._app_config?.switchTab) window._app_config.switchTab("timetable");
    });
  });

  // Map triggers
  container.querySelectorAll(".map-trigger").forEach((btn) => {
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      openMap(btn.dataset.stopCode);
    });
  });

  // Switch tab triggers (e.g. alert badges)
  container.querySelectorAll("[data-switch-to-tab]").forEach((el) => {
    if (el.__has_click) return;
    el.__has_click = true;
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      const tab = el.dataset.switchToTab;
      if (tab && window._app_config?.switchTab) {
        window._app_config.switchTab(tab);
      }
    });
  });

  // Direction toggle
  container.querySelectorAll("[data-dir]").forEach((button) => {
    if (button.closest("[data-timetable-direction]")) return;
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      const { saveSettings: save } = getArgs();
      save({ liveDirection: button.dataset.dir });
    });
  });

  // Card expand/collapse
  container.querySelectorAll("[data-toggle-card]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      const card = container.querySelector(`[data-card="${button.dataset.toggleCard}"]`);
      card?.classList.toggle("expanded");
    });
  });

  // Stop search
  const searchInput = container.querySelector("[data-stop-search]");
  const resultsBox = container.querySelector("[data-stop-results]");
  if (searchInput && resultsBox) {
    const handleStopPick = async (stopCode) => {
      const { state: s, lineData: ld, lineConfig: lc, cfg: c, saveSettings: save } = getArgs();
      try {
        let history = JSON.parse(localStorage.getItem("trasporti_search_history") || "[]");
        history = [stopCode, ...history.filter((c2) => c2 !== stopCode)].slice(0, 3);
        localStorage.setItem("trasporti_search_history", JSON.stringify(history));
      } catch (_e) {
        /* ignore */
      }

      let stopIsServed = false;
      for (const lineId of Object.keys(ld || {})) {
        const data = ld[lineId];
        for (const key of Object.keys(data || {})) {
          if ((data[key] || []).some((t) => t.stops?.[stopCode] !== undefined)) {
            stopIsServed = true;
            break;
          }
        }
        if (stopIsServed) break;
      }
      if (!stopIsServed) {
        try {
          const { loadLines, getCachedLineData } = await import("./line-registry.js");
          await loadLines(Object.keys(lc));
          Object.assign(ld, getCachedLineData());
          s.showAllLines = true;
        } catch (_e) {
          console.error("[Trasporti] Errore caricamento linee:", _e);
        }
      }
      s.liveStopFilter = stopCode;
      renderLive(s, ld, lc, c, save);
    };

    if (!searchInput.__has_input) {
      searchInput.__has_input = true;
      searchInput.addEventListener("input", () => {
        const q = searchInput.value.trim();
        if (q.length >= 1) {
          resultsBox.innerHTML = buildSearchResults(q);
          resultsBox.classList.add("open");
          resultsBox.querySelectorAll("[data-stop-pick]").forEach((btn) => {
            if (btn.__has_click) return;
            btn.__has_click = true;
            btn.addEventListener("click", () => handleStopPick(btn.dataset.stopPick));
          });
        } else {
          resultsBox.innerHTML = "";
          resultsBox.classList.remove("open");
        }
      });
    }

    if (!searchInput.__has_focus) {
      searchInput.__has_focus = true;
      searchInput.addEventListener("focus", () => {
        const { state: s } = getArgs();
        if (!searchInput.value && !s.liveStopFilter) {
          resultsBox.innerHTML = buildSearchResults(" ");
          resultsBox.classList.add("open");
          resultsBox.querySelectorAll("[data-stop-pick]").forEach((btn) => {
            if (btn.__has_click) return;
            btn.__has_click = true;
            btn.addEventListener("click", () => handleStopPick(btn.dataset.stopPick));
          });
        } else if (searchInput.value) {
          searchInput.value = "";
          searchInput.dispatchEvent(new Event("input"));
        }
      });
    }

    if (!document.__has_results_click) {
      document.__has_results_click = true;
      document.addEventListener(
        "click",
        (e) => {
          document.__has_results_click = false;
          if (!e.target.closest(".stop-search-wrapper")) {
            resultsBox.classList.remove("open");
          }
        },
        { once: true }
      );
    }

    const chipsWrapper = container.querySelector("#search-chips-wrapper");
    if (chipsWrapper) {
      bindChipEvents(chipsWrapper, handleStopPick, getSearchHistoryChipsHTML);
    }
  }

  // Clear stop search
  const clearBtn = container.querySelector("[data-stop-search-clear]");
  if (clearBtn && !clearBtn.__has_click) {
    clearBtn.__has_click = true;
    clearBtn.addEventListener("click", () => {
      const { state: s, lineData: ld, lineConfig: lc, cfg: c, saveSettings: save } = getArgs();
      s.liveStopFilter = null;
      renderLive(s, ld, lc, c, save);
    });
  }

  // Line filter pills
  container.querySelectorAll("[data-line-filter]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", async () => {
      const { state: s, lineData: ld, lineConfig: lc, cfg: c, saveSettings: save } = getArgs();
      const lineId = button.dataset.lineFilter || null;
      if (lineId && !ld[lineId] && lc[lineId]) {
        try {
          const { loadSingleLine } = await import("./line-registry.js");
          const data = await loadSingleLine(lineId);
          if (data) ld[lineId] = data;
        } catch (_e) {
          console.error(`[Trasporti] Errore caricamento ${lineId}:`, _e);
        }
      }
      s.liveLineFilter = lineId;
      renderLive(s, ld, lc, c, save);
    });
  });

  // Clear all filters
  const clearFiltersBtn = container.querySelector("[data-clear-filters]");
  if (clearFiltersBtn && !clearFiltersBtn.__has_click) {
    clearFiltersBtn.__has_click = true;
    clearFiltersBtn.addEventListener("click", () => {
      const { state: s, lineData: ld, lineConfig: lc, cfg: c, saveSettings: save } = getArgs();
      s.liveStopFilter = null;
      s.liveLineFilter = null;
      renderLive(s, ld, lc, c, save);
    });
  }

  // Toggle show all lines
  const toggleLinesBtn = container.querySelector("[data-toggle-all-lines]");
  if (toggleLinesBtn && !toggleLinesBtn.__has_click) {
    toggleLinesBtn.__has_click = true;
    toggleLinesBtn.addEventListener("click", async () => {
      const { state: s, lineData: ld, lineConfig: lc, cfg: c, saveSettings: save } = getArgs();
      s.showAllLines = !s.showAllLines;
      if (s.showAllLines) {
        try {
          const { loadLines, getCachedLineData } = await import("./line-registry.js");
          await loadLines(Object.keys(lc));
          Object.assign(ld, getCachedLineData());
        } catch (_e) {
          console.error("[Trasporti] Errore caricamento tutte le linee:", _e);
        }
      }
      renderLive(s, ld, lc, c, save);
    });
  }

  // Dismiss strike alerts
  container.querySelectorAll("[data-dismiss-alert]").forEach((btn) => {
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", () => {
      dismissAlert(btn.dataset.dismissAlert);
      const banner = btn.closest(".alert-strike-banner");
      if (banner) banner.remove();
    });
  });

  // View train timetable
  container.querySelectorAll(".view-train-timetable-btn").forEach((btn) => {
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      state.trainsStation = btn.dataset.stationCode;
      if (window._app_config?.switchTab) window._app_config.switchTab("trains");
    });
  });

  // Customize modal
  const openBtn = container.querySelector(".live-customize-trigger");
  const modal = container.querySelector("#live-customize-modal");
  if (openBtn && modal && !openBtn.__has_click) {
    openBtn.__has_click = true;
    openBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      modal.classList.add("open");
    });
  }

  if (modal) {
    modal.querySelectorAll(".live-customize-close, .live-customize-close-btn").forEach((btn) => {
      if (btn.__has_click) return;
      btn.__has_click = true;
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        modal.classList.remove("open");
      });
    });

    if (!modal.__has_backdrop_click) {
      modal.__has_backdrop_click = true;
      modal.addEventListener("click", (e) => {
        if (e.target === modal) modal.classList.remove("open");
      });
    }

    const saveBtn = modal.querySelector("#save-live-customization");
    if (saveBtn && !saveBtn.__has_click) {
      saveBtn.__has_click = true;
      saveBtn.addEventListener("click", () => {
        const { saveSettings: save } = getArgs();
        const heroSelect = modal.querySelector("#customize-live-hero");
        const liveHero = heroSelect ? heroSelect.value : "Z649";
        save({ liveHero });
        modal.classList.remove("open");
        showPremiumToast("Scheda HERO aggiornata con successo!", "success");
      });
    }

    const resetBtn = modal.querySelector("#reset-live-lines");
    if (resetBtn && !resetBtn.__has_click) {
      resetBtn.__has_click = true;
      resetBtn.addEventListener("click", () => {
        if (confirm("Ripristinare le linee predefinite per questa città?")) {
          const { state: s, saveSettings: save } = getArgs();
          const followedLinesByCity = structuredClone(s.settings.followedLinesByCity || {});
          delete followedLinesByCity[focusCity];
          save({ followedLinesByCity });
          modal.classList.remove("open");
          showPremiumToast("Linee ripristinate ai valori predefiniti per questa città!", "success");
        }
      });
    }
  }
}

/**
 * Show a premium floating toast notification.
 * Appended to document.body so it survives reactive DOM patching.
 */
export function showPremiumToast(message, type = "success") {
  let container = document.getElementById("premium-toast-container");
  if (!container) {
    container = document.createElement("div");
    container.id = "premium-toast-container";
    container.style.cssText =
      "position:fixed;bottom:24px;left:50%;transform:translateX(-50%) translateY(40px);z-index:10000;display:flex;flex-direction:column;gap:8px;pointer-events:none;width:max-content;max-width:90vw;transition:transform 0.4s cubic-bezier(0.175,0.885,0.32,1.275);";
    document.body.appendChild(container);
  }

  const toast = document.createElement("div");
  toast.className = `toast-item ${type}`;
  toast.style.cssText =
    "background:rgba(var(--surface-rgb),0.9);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);border:1px solid var(--line);box-shadow:var(--shadow);border-radius:12px;padding:12px 20px;color:var(--text);font-size:0.88rem;font-weight:600;display:flex;align-items:center;gap:10px;opacity:0;transform:translateY(20px);transition:all 0.35s cubic-bezier(0.175,0.885,0.32,1.275);pointer-events:auto;";
  if (type === "success") toast.style.borderLeft = "4px solid var(--ok, #22c55e)";
  else if (type === "info") toast.style.borderLeft = "4px solid var(--accent, #22d3ee)";

  const icon =
    type === "success"
      ? `<span style="color:var(--ok,#22c55e);font-size:1.1rem;display:inline-flex;align-items:center;justify-content:center;font-weight:bold;">✓</span>`
      : `<span style="color:var(--accent,#22d3ee);font-size:1.1rem;display:inline-flex;align-items:center;justify-content:center;font-weight:bold;">ℹ</span>`;
  toast.innerHTML = `${icon}<span style="letter-spacing:-0.01em;">${message}</span>`;
  container.appendChild(toast);
  container.style.transform = "translateX(-50%) translateY(0)";
  setTimeout(() => {
    toast.style.opacity = "1";
    toast.style.transform = "translateY(0)";
  }, 10);
  setTimeout(() => {
    toast.style.opacity = "0";
    toast.style.transform = "translateY(-20px)";
    setTimeout(() => {
      toast.remove();
      if (container.children.length === 0)
        container.style.transform = "translateX(-50%) translateY(40px)";
    }, 350);
  }, 4000);
}
